import React, { createContext, useContext, useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { GraphQLErrors } from "@apollo/client/errors";
import {
  getViewer_viewer_lists as viewerLists,
  getViewer_viewer as Viewer,
} from "types/gql-types";
import { initialStepData, StepData } from ".";

type StepContextType = {
  stepData: StepData;
  setStepData: React.Dispatch<React.SetStateAction<StepData>>;
  viewer: Viewer;
  stepName: string;
  setStepName: React.Dispatch<React.SetStateAction<string>>;
  importErrors: GraphQLErrors;
  setImportErrors: React.Dispatch<React.SetStateAction<GraphQLErrors>>;
};

const StepContext = createContext<StepContextType | undefined>(undefined);

export const useStepData = () => {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error("useStepData must be used within a StepProvider");
  }
  if (!context.viewer) {
    throw new Error("useStepData must be used within a StepProvider with viewer");
  }
  if (!context.viewer.lists) {
    throw new Error("useStepData must be used within a StepProvider with viewer.lists");
  }
  if (!context.viewer.lists.length) {
    throw new Error("useStepData must be used within a StepProvider with an accessible list");
  }
  return context;
};

export const getListName = (lists?: viewerLists[] | null, listId?: string): string => {
  if (!lists) {
    return "Unknown List";
  }

  if (!listId) {
    if (lists.length === 1) {
      return lists[0].name;
    }
    return "No List Selected";
  }

  return lists.find(list => list.id === listId)?.name || "Unknown List";
};
export const StepProvider: React.FC<{ children: React.ReactNode; viewer: Viewer }> = ({ children, viewer }) => {
  const [stepData, setStepData] = useState<StepData>(initialStepData);
  const [importErrors, setImportErrors] = useState<GraphQLErrors>([]);
  const [stepName, setStepName] = useState<string>("Upload");
  const posthog = usePostHog();

  useEffect(() => {
    posthog.identify(viewer.id, {
      organization: viewer.organizationId,
      email: viewer.email,
    });
    posthog.capture("import_step", {
      stepName,
      listName: getListName(viewer.lists, stepData.listId),
    });
  }, [stepName]);

  return (
    <StepContext.Provider value={{ stepData, setStepData, viewer, stepName, setStepName, importErrors, setImportErrors }}>
      {children}
    </StepContext.Provider>
  );
};
